import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
  OnChanges,
  SimpleChanges
} from "@angular/core";
import { AsyncPipe, CommonModule, JsonPipe } from "@angular/common";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { FlexModule } from "@angular/flex-layout/flex";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatCardModule } from "@angular/material/card";
import {
  AppCalculator,
  Application,
  BrokerageSelectionValue,
  GetRateCardDetailsFn,
  LoanPurposeValue,
  PaymentFrequencyValue,
  PaymentFrequencyValueOptions,
  RatecardDetails,
  SliderComponentValue,
  TermRate,
  TotalPaymentBreakupDialogData,
  UpdateApplicationData,
  UpdateApplicationFn,
  UpdateApplicationSfFn,
  YesNoValue,
  booleanToYesNo,
  getBrokerage,
  getInterestRate,
  isAdminOrCreditUserOrSalesAM,
  isInternalUser,
  calculateBusinessLoanEstimation,
  PaymentFrequencyType,
  LoanTermType,
  BrokerageSelectionType,
  GetContractStatusFn,
  isAdminOrCreditUser,
  isAdmin,
  isAdminOrCreditOrOperationsOrSalesUser,
  CurrencyInputValue,
  LoanTermsSelectionWithInputValue,
  getRepaymentType,
  isCreditUser,
  ApplicationTypes,
  MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT,
  BUSINESS_LOAN_MAX_BROKERAGE_PERCENT,
} from "@portal-workspace/grow-shared-library";
import { MessageBoxComponent } from '../../message-box/message-box.component';
import {Subject, Subscription, tap} from "rxjs";
import { ApplicationDialogService } from "../../application-dialog-component/application-dialog.service";
import { PortalHotToastService } from "../../portal-hot-toast-component/hot-toast.service";
import { loadingFor } from "@ngneat/loadoff";
import {
  applicationToAbnAgeAboveThreshold,
  applicationToAdverseOnFile,
  applicationToBrokerageAmount,
  applicationToBusinessSearchValue,
  applicationToCrediRateAdjustment,
  applicationToDocFee,
  applicationToDocFeeFinanced,
  applicationToEquifaxDirectorScoreMeetsThreshold,
  applicationToGstAgeMonths,
  applicationToGstAgeMonthsWithContinuity,
  applicationToLastUpdatedByUser,
  applicationToLoanAmount,
  applicationToLoanPurpose,
  applicationToLoanTerms,
  applicationToLoanTermsValue,
  applicationToPaymentFrequency,
  applicationToPropertyOwner,
  getUser,
} from "@portal-workspace/grow-ui-library";
import { DisableControlDirective } from '../../../directives/disable-control.directive';
import { InputMaskModule } from "@ngneat/input-mask";
import { BrokerageSelectionComponent } from '../../brokerage-component/brokerage-selection.component';
import { YesNoComponent } from '../../yes-no-component/yes-no.component';
import { PaymentFrequencySelectionComponent } from '../../payment-frequency-selection-component/payment-frequency-selection.component';
import { BusinessLoanTermsSelectionComponent } from '../../loan-terms-selection-component/business-loan-terms-selection.component';
import { CurrencyInputComponent } from '../../currency-selection-component/currency-input.component';
import { MatInputModule } from "@angular/material/input";
import { LoanPurposeSelectionComponent } from '../../loan-purpose-selection-component/loan-purpose-selection.component';
import moment, { Moment } from "moment";
import numeral from "numeral";
import { UntilDestroy } from "@ngneat/until-destroy";
import { MatTableModule } from "@angular/material/table";
import { PercentageInputComponent } from '../../percentage-input-component/percentage-input.component';
import _ from "lodash";
import {LooseCurrencyPipe} from '../../../pipes/loose-currency.pipe';
import {PercentagePipe} from '../../../pipes/percentage.pipe';
import {MarkDirective} from '../../../directives/mark-as-dirty.directive';
import { LoanTermsSelectionWithInputComponent } from '../../loan-terms-selection-component/loan-terms-selection-with-input.component';

export type BusinessPricingComponentEvent = BusinessPricingComponentSavedEvent;

export interface BusinessPricingComponentSavedEvent {
  type: 'business-pricing-saved',
  rst: TotalPaymentBreakupDialogData
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'business-pricing',
  templateUrl: './business-pricing.component.html',
  styleUrls: ['./business-pricing.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MatButtonModule,
    MatFormFieldModule,
    AsyncPipe,
    JsonPipe,
    MatCardModule,
    MessageBoxComponent,
    DisableControlDirective,
    InputMaskModule,
    YesNoComponent,
    BrokerageSelectionComponent,
    PaymentFrequencySelectionComponent,
    BusinessLoanTermsSelectionComponent,
    CurrencyInputComponent,
    MatInputModule,
    LoanPurposeSelectionComponent,
    MatTableModule,
    PercentageInputComponent,
    LooseCurrencyPipe,
    MarkDirective,
    PercentagePipe,
    LoanTermsSelectionWithInputComponent,
 ]
})
export class BusinessPricingComponent implements OnInit, OnChanges {

  markSubject = new Subject<boolean>();

  @Input({ required: true }) getRateCardDetailsFn!: GetRateCardDetailsFn;
  @Input({ required: true }) updateApplicationFn!: UpdateApplicationFn;
  @Input({ required: true }) updateApplicationSfFn!: UpdateApplicationSfFn;
  @Input({required: true}) getContractStatusFn!: GetContractStatusFn;
  @Input({ required: true }) application!: Application;
  @Input({required:true}) currentRstValue!: TotalPaymentBreakupDialogData;
  @Output() events = new EventEmitter<BusinessPricingComponentEvent>();
  @ViewChild('readMode') readModeTemplate!: TemplateRef<any>;
  @ViewChild('editMode') editModeTemplate!: TemplateRef<any>;

  subscriptions: Subscription[] = [];

  formBuilder: FormBuilder = inject(FormBuilder);
  applicationDialogService: ApplicationDialogService = inject(ApplicationDialogService);
  toastService: PortalHotToastService = inject(PortalHotToastService);

  loader = loadingFor('saveRate');

  // credit adjustment
  ADMIN_MIN_CREDIT_RATE = -10000;
  ADMIN_MAX_CREDIT_RATE = 10000;
  CREDIT_MIN_CREDIT_RATE = -5;
  CREDIT_MAX_CREDIT_RATE = 5;
  NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE = -1;
  NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE = 5;
  MIN_CREDIT_RATE = this.ADMIN_MIN_CREDIT_RATE;
  MAX_CREDIT_RATE = this.ADMIN_MAX_CREDIT_RATE;


  MIN_LOAN_AMOUNT = 0.01;
  step2MinLimit: number | null = this.MIN_LOAN_AMOUNT;
  step2MaxLimit: number | null = null;
  step2MinBrokerageAmount: number = 0;
  step2MaxBrokerageAmount: number = 999999;
  step2Repayment: string = '';
  step2EstimatedDrawdownDate: Moment = moment();

  AmountFinance!: number;
  rateCard: RatecardDetails | null = null;
  terms: TermRate | null = null;              // available after chart popups (after onGenerateQuote())
  calculator: AppCalculator | null = null;    // available after chart popups (after onGenerateQuote())
  rst: TotalPaymentBreakupDialogData | null = null;    // calculate estimation result (available after chart popups (after onGenerateQuote())
  data: Partial<UpdateApplicationData> | null = null;  // available after chart popups (after onGenerateQuote())
  lastEditedByUser ?: string | null

  user = getUser();
  isInternalUser = isInternalUser(this.user)
  isAdminOrCreditUserOrSalesAM = isAdminOrCreditUserOrSalesAM(this.user);
  canEditPricingByRole = isAdminOrCreditOrOperationsOrSalesUser(this.user);
  isNotAdminOrCreditUser = !isAdminOrCreditUser(this.user);
  isAdmin = isAdmin(this.user);
  isCreditUser = isCreditUser(this.user);

  isContractPending: boolean = true; // if a contract is pending for this application
  isApplicationStagePostQa = true;

  dataSource: (
    {name: string, highlight?: (value: string | null | undefined) => boolean, type: 'text', value: string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'currency', value: number | string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'percentage', value: number | string | null | undefined}
  )[] = [];
  currentTemplate: TemplateRef<any> | null = null;

  formGroupStep2!: FormGroup<{
    loanPurpose: FormControl<LoanPurposeValue | null>,
    loanTerms: FormControl<LoanTermsSelectionWithInputValue>,
    loanAmount: FormControl<SliderComponentValue>,
    paymentFrequency: FormControl<PaymentFrequencyValue>,
    brokerageAmount: FormControl<CurrencyInputValue>,
    propertyOwner: FormControl<YesNoValue>,
    adverseOnFile: FormControl<YesNoValue>,
    equifaxScore: FormControl<YesNoValue>,
    abnGstAgeAboveThreshold: FormControl<YesNoValue>,
    creditRateAdjustment: FormControl<number | null>,
    docFeeFinanced: FormControl<YesNoValue>,
  }>;
  formControlStep2LoanPurpose!: FormControl<LoanPurposeValue | null>;
  formControlStep2LoanAmount!: FormControl<SliderComponentValue>;
  formControlStep2LoanTerms!: FormControl<LoanTermsSelectionWithInputValue>;
  formControlStep2BrokerageAmount!: FormControl<CurrencyInputValue>;
  formControlStep2PaymentFrequency!: FormControl<PaymentFrequencyValue>;
  formControlStep2PropertyOwner!: FormControl<YesNoValue>;
  formControlStep2AdverseOnFile!: FormControl<YesNoValue>;
  formControlStep2EquifaxDirectorScoreAbove700!: FormControl<YesNoValue>;
  formControlAbnAgeAboveThreshold!: FormControl<YesNoValue>;
  formControlCreditRateAdjustment!: FormControl<number | null>;
  formControlStep2DocFeeFinanced!: FormControl<YesNoValue>;

  applicationType: ApplicationTypes = 'BusinessLoans';

  ngOnInit() {
    this.formControlStep2LoanPurpose = this.formBuilder.control<LoanPurposeValue | null>(null, [Validators.required]);
    this.formControlStep2LoanAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlStep2LoanTerms = this.formBuilder.control(null, [Validators.required]);
    this.formControlStep2BrokerageAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlStep2PaymentFrequency = this.formBuilder.control({ type: 'Weekly', name: 'Weekly' }, [Validators.required]);
    this.formControlStep2PropertyOwner = this.formBuilder.control(true, [Validators.required]);
    this.formControlStep2AdverseOnFile = this.formBuilder.control(false, [Validators.required]);
    this.formControlStep2EquifaxDirectorScoreAbove700 = this.formBuilder.control(true, [Validators.required]);
    this.formControlAbnAgeAboveThreshold = this.formBuilder.control(true, [Validators.required]);
    this.formControlCreditRateAdjustment = this.formBuilder.control(0.0, [Validators.required]);
    this.formControlStep2DocFeeFinanced = this.formBuilder.control(true, [Validators.required]);

    this.formGroupStep2 = this.formBuilder.group({
      loanPurpose: this.formControlStep2LoanPurpose,
      loanTerms: this.formControlStep2LoanTerms,
      loanAmount: this.formControlStep2LoanAmount,
      brokerageAmount: this.formControlStep2BrokerageAmount,
      paymentFrequency: this.formControlStep2PaymentFrequency,
      propertyOwner: this.formControlStep2PropertyOwner,
      adverseOnFile: this.formControlStep2AdverseOnFile,
      equifaxScore: this.formControlStep2EquifaxDirectorScoreAbove700,
      abnGstAgeAboveThreshold: this.formControlAbnAgeAboveThreshold,
      creditRateAdjustment: this.formControlCreditRateAdjustment,
      docFeeFinanced: this.formControlStep2DocFeeFinanced,
    });

    //populate
    this.populate();

    this.createReadonlyTableDataSource();


    if (this.isNotAdminOrCreditUser) { // not credit or admin
      this.formControlStep2PropertyOwner.disable();
      this.formControlStep2AdverseOnFile.disable();
      this.formControlStep2EquifaxDirectorScoreAbove700.disable();

      this.formControlAbnAgeAboveThreshold.disable();

      const loanAmount = applicationToLoanAmount(this.application) ?? 0;
      const adjustmentToLoanAmount = (loanAmount * 0.1);
      const maxLoanAmount = _.round(loanAmount + adjustmentToLoanAmount);
      this.step2MaxLimit = maxLoanAmount;

    } else { // credit or admin
    }



    // ZEN-818 follow-up: "Director Score 500-550" toggle removed; the Equifax Director Score
    // toggle now directly drives the tier calculation.

    this.subscriptions.push(this.formControlStep2LoanAmount.valueChanges.pipe(
      tap(loanAmount => {
        this.step2MaxBrokerageAmount = Math.round((loanAmount ?? 0) * BUSINESS_LOAN_MAX_BROKERAGE_PERCENT) / 100;
      })
    ).subscribe());

    this.subscriptions.push(this.getRateCardDetailsFn(this.application.UserId, 'BusinessLoans').pipe(
      tap(rateCardDetails => {
        this.rateCard = rateCardDetails;
      })
    ).subscribe());


    // get contract status for application
    this.subscriptions.push(this.getContractStatusFn(this.application.ApplicationId).pipe(
      tap(r => {
        const {contractPending} = r.signers.reduce((acc, signer) => {
          const contractPending = ["created", "sent", "delivered", "signed", "completed", "faxpending"].includes(signer.status);
          acc.contractPending = acc.contractPending || contractPending;
          return acc;
        }, { contractPending: false});
        this.isContractPending = contractPending;
      })
    ).subscribe());

    this.isApplicationStagePostQa = ['QA', 'Settlement', 'Closed Lost', 'Closed Lost'].includes(this.application.InteflowStatusStageName ?? '');
  }

  populate() {
    this.formControlStep2LoanPurpose.setValue(applicationToLoanPurpose(this.application));
    this.formControlStep2LoanAmount.setValue(applicationToLoanAmount(this.application));
    this.formControlStep2LoanTerms.setValue(applicationToLoanTermsValue(this.application));
    this.formControlStep2PaymentFrequency.setValue(applicationToPaymentFrequency(this.application) ??
      PaymentFrequencyValueOptions.find(o => o.type === 'Monthly') ?? null);
    this.formControlStep2PropertyOwner.setValue(applicationToPropertyOwner(this.application));
    this.formControlStep2AdverseOnFile.setValue(applicationToAdverseOnFile(this.application));
    this.formControlStep2EquifaxDirectorScoreAbove700.setValue(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=700'));
    this.formControlCreditRateAdjustment.setValue(applicationToCrediRateAdjustment(this.application));
    this.formControlAbnAgeAboveThreshold.setValue(applicationToAbnAgeAboveThreshold(this.application));
    this.formControlStep2DocFeeFinanced.setValue(applicationToDocFeeFinanced(this.application));
    const calculator = new AppCalculator();
    const brokerageAmount = applicationToBrokerageAmount(this.application) == null ?
    calculator.calculateBrokerageAmount(applicationToLoanAmount(this.application) ?? 0, getBrokerage(this.application)):
    applicationToBrokerageAmount(this.application);
    this.formControlStep2BrokerageAmount.setValue(brokerageAmount);
  }

  createReadonlyTableDataSource() {
    this.lastEditedByUser = applicationToLastUpdatedByUser(this.application)?.name;
    const calculator = new AppCalculator();
    const brokerageAmount = applicationToBrokerageAmount(this.application) == null ?
      calculator.calculateBrokerageAmount(applicationToLoanAmount(this.application) ?? 0, getBrokerage(this.application)) :
      applicationToBrokerageAmount(this.application);

    this.dataSource = [
      { name: 'Loan Purpose', type: 'text', value: applicationToLoanPurpose(this.application)?.name},
      { name: 'Loan Amount ($)', type: 'currency', value: applicationToLoanAmount(this.application) },
      { name: 'Loan Terms', type: 'text', value: `${applicationToLoanTerms(this.application)?.type ?? ''} months` },
      { name: 'Payment Frequency', type: 'text', value: applicationToPaymentFrequency(this.application)?.type},
      { name: 'Brokerage ($)', type: 'currency', value: brokerageAmount },

      { name: 'Property Owner', type: 'text', value: booleanToYesNo(applicationToPropertyOwner(this.application)) },
      { name: 'Adverse on file', type: 'text', value: booleanToYesNo(applicationToAdverseOnFile(this.application)) },
      { name: 'Equifax Director Score ≥ 700', type: 'text', value: booleanToYesNo(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=700')) },
      { name: 'Doc Fee Financed', type: 'text', value: booleanToYesNo(applicationToDocFeeFinanced(this.application))},
      { name: 'Doc Fee ($)', type: 'currency', value: applicationToDocFee(this.application) },

      { name: 'Interest Rate (%)', type: 'percentage', value: getInterestRate(this.application) },
      { name: 'ABN age >= 24 months', type: 'text', value: booleanToYesNo(applicationToAbnAgeAboveThreshold(this.application)) },

      { highlight: (v) => Number(v) != 0, name: 'Credit Rate Adjustment (%)', type: 'percentage', value: applicationToCrediRateAdjustment(this.application) },
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['application'];
    if (change) {
      this.createReadonlyTableDataSource();
    }
  }

  ngAfterViewInit() {
    this.currentTemplate = this.readModeTemplate;
  }

  switchMode() {
    if (this.currentTemplate == this.editModeTemplate) {
      this.currentTemplate = this.readModeTemplate;
    } else {
      this.populate();
      this.currentTemplate = this.editModeTemplate;
    }
  }

  calculateEstimation(date: Moment): TotalPaymentBreakupDialogData {
    const paymentFrequencyValue: PaymentFrequencyValue = this.formControlStep2PaymentFrequency.value;
    const paymentFrequencyType: PaymentFrequencyType = paymentFrequencyValue?.type ?? 'Monthly';
    const loanTermValue: LoanTermsSelectionWithInputValue = this.formControlStep2LoanTerms.value;
    const loanTermType: LoanTermType | null = loanTermValue ? String(loanTermValue) as LoanTermType : null;
    const loanAmount = numeral(this.formControlStep2LoanAmount.value).value() ?? 0;
    const businessSearchValue = applicationToBusinessSearchValue(this.application);
    const propertyOwner = this.formControlStep2PropertyOwner.value ?? false;
    const brokerageAmount = this.formControlStep2BrokerageAmount.value ?? 0;
    const brokeragePercentage = Math.round((brokerageAmount ?? 0) * 10000 / loanAmount) / 100;
    const brokerageValue: BrokerageSelectionValue = {type: `${brokeragePercentage}` as BrokerageSelectionType, name: `${brokeragePercentage}%`};
    const brokerageType: BrokerageSelectionType = brokerageValue?.type ?? '0';
    const brokerage = Number(brokerageValue?.type ?? 0);
    const adverseOnFile = this.formControlStep2AdverseOnFile.value ?? false;
    const equifaxDirectorScoreAbove700 = this.formControlStep2EquifaxDirectorScoreAbove700.value ?? false;
    const gstAgeMonths = applicationToGstAgeMonths(this.application);
    const gstAgeMonthsWithContinuity = applicationToGstAgeMonthsWithContinuity(this.application);
    const creditRateAdjustment = this.formControlCreditRateAdjustment.value ?? 0;
    const docFeeFinanced = this.formControlStep2DocFeeFinanced.value ?? false;
    
    // Calculate ABN age threshold override: only set when user has changed from original
    const abnAgeAboveThresholdOverride = this.getAbnAgeAboveThresholdOverride();

    const r = calculateBusinessLoanEstimation(date, {
      type: 'BusinessLoans',
      paymentFrequencyType,
      loanTermType,
      brokerageType,
      loanAmount,
      businessSearchValue: (businessSearchValue?.type === 'search-result' ? businessSearchValue : null),
      propertyOwner,
      brokerage,
      brokerageAmount,
      adverseOnFile,
      equifaxDirectorScoreAbove700,
      gstAgeMonths,
      gstAgeMonthsWithContinuity,
      rateCard: this.rateCard,
      bureauReport: this.application.CompanyDetails ?? null,
      creditRateAdjustment,
      docFeeFinanced,
      repaymentType : getRepaymentType(this.application) ?? '',
      abnAgeAboveThresholdOverride,
    });
    this.calculator = r.calculator;
    this.step2Repayment = String(r.repayment);
    this.terms = r.terms
    this.data = this.createApplicationData();
    this.rst = r.totalPaymentBreakupDialogData
    return r.totalPaymentBreakupDialogData;
  }

  //   } : {} as any /* HACK */

  //     // extra field that affects estimation
  //     creditRateAdjustment: creditRateAdjustment ?? 0, // handle creditRateAdjustment

  /**
   * Returns the ABN age threshold override value:
   * - undefined: use actual ABN age from database (no override)
   * - true: treat as >= 24 months threshold (no ABN age penalty)
   * - false: treat as < 24 months threshold (apply ABN age penalty)
   */
  getAbnAgeAboveThresholdOverride(): boolean | undefined {
    const userSelectedAboveThreshold = this.formControlAbnAgeAboveThreshold.value;
    const originalAbnAge = this.application.CompanyDetails?.ABNAgeMonths ?? 0;
    const originalAboveThreshold = originalAbnAge >= 24;
    
    // Only return override if user has changed from the original value
    if (userSelectedAboveThreshold === originalAboveThreshold) {
      return undefined; // No override needed, use actual ABN age
    }
    
    return userSelectedAboveThreshold ?? undefined;
  }


  createApplicationData() {
    // NOTE: changes done here needs to be done in business-finance.page.ts as well
    // Keep original CompanyDetails without modifying ABN age
    // The ABN age threshold override is handled by the calculation via abnAgeAboveThresholdOverride parameter
    const companyDetails = this.application.CompanyDetails;

    // pricing details
    const loanTerm: LoanTermsSelectionWithInputValue = this.formControlStep2LoanTerms.value;
    const loanAmount = _.round(this.formControlStep2LoanAmount.value ?? 0, 2);
    const brokerageAmount = this.formControlStep2BrokerageAmount.value;
    const brokeragePercentage = Math.round((brokerageAmount ?? 0) * 10000 / loanAmount) / 100;
    const brokerage: BrokerageSelectionValue = {type: `${brokeragePercentage}` as BrokerageSelectionType, name: `${brokeragePercentage}%`};
    const paymentPeriod: PaymentFrequencyValue = this.formControlStep2PaymentFrequency.value;
    const docFee = this.terms?.docFee ?? undefined;
    const brokerOriginationFee = this.terms?.brokerOriginationFee ?? 0;
    const propertyOwner = this.formControlStep2PropertyOwner.value;
    const adverseOnFile = this.formControlStep2AdverseOnFile.value;
    const equifaxDirectorScoreAbove700 = this.formControlStep2EquifaxDirectorScoreAbove700.value;
    const loanPurpose: LoanPurposeValue | null = this.formControlStep2LoanPurpose.value;
    const repayment = this.calculator?.emiAmt ?? undefined;
    const rate = this.terms ? this.terms?.totalInterest : getInterestRate(this.application);
    const docFeeFinanced = !!this.formControlStep2DocFeeFinanced.value;

    let apiBodyPricingDetails = {
      ...this.application.PricingDetails,
      LoanTerm: loanTerm ?? undefined,
      Brokerage: brokerage ? numeral(brokerage.type).value() ?? undefined : undefined,
      BrokerageAmount: brokerageAmount,
      BrokerOriginationFee: brokerOriginationFee,
      LoanAmount: loanAmount,
      AmountFinance: loanAmount,
      PaymentPeriod: paymentPeriod ? paymentPeriod.name : undefined,
      DocFee: (docFee != null ? Number(docFee) : undefined),
      DocFeeFinanced: booleanToYesNo(docFeeFinanced),
      // ZEN-818 follow-up: BL now writes only the new threshold field; the legacy
      // boolean would mis-classify 600-699 applicants when set from a >=700 toggle.
      EquifaxDirectorScoreThreshold: equifaxDirectorScoreAbove700 ? '>=700' : '<700',
      Repayment: repayment,
      PropertyOwner: booleanToYesNo(propertyOwner),
      AdverseOnFile: booleanToYesNo(adverseOnFile),
      Rate: rate,
      CreditRateAdjustment: Number(this.formControlCreditRateAdjustment.value),
      AbnAgeAboveThresholdOverride: this.getAbnAgeAboveThresholdOverride() === undefined
        ? null
        : booleanToYesNo(this.getAbnAgeAboveThresholdOverride())
    }

    const apiBodyAppInfo = {
      ...this.application.AppInfo,
      LoanPurpose: loanPurpose,
    }

    const apiBody: Partial<UpdateApplicationData> = {
      UpdateApplicationIgnoreStatus: true,
      PricingDetails: apiBodyPricingDetails as any,
      CompanyDetails: companyDetails as any,
      AppInfo: apiBodyAppInfo,
    }
    return apiBody;
  }

  save() {
    if (this.terms) {
      this.subscriptions.push(this.updateApplicationSfFn(this.application.ApplicationId, this.data as UpdateApplicationData).pipe(
        this.toastService.spinnerObservable(),
        this.toastService.snackBarObservable(`Rate updated`),
        tap(r => {
          if (r.status) {
            this.events.emit({
              type: "business-pricing-saved",
              rst: this.rst!,
            });
            this.currentTemplate = this.readModeTemplate;
          }
        })
      ).subscribe());
    } else {
      this.applicationDialogService.openAlertDialog({
        message: 'Error',
        subMessage: `Cannot saved without performing calculation first`,
      });
    }
  }

  onGenerateQuote() {
    // Warn (but do not block) when the loan amount is below the policy minimum.
    // Calculators and submission steppers hard-block; the pricing tab is left
    // soft-gated so existing sub-minimum applications can still be re-priced.
    // Only Credit and Admin users can override and continue below the minimum.
    const loanAmount = this.formControlStep2LoanAmount.value ?? 0;
    if (loanAmount > 0 && loanAmount < MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT) {
      if (this.isNotAdminOrCreditUser) {
        this.applicationDialogService.openAlertDialog({
          message: 'Loan amount below minimum',
          subMessage: `The requested limit is below the $${MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT.toLocaleString()} minimum for Business Term Loans. Only Credit and Admin users can override this.`,
        });
        return;
      }
      this.subscriptions.push(
        this.applicationDialogService.openConfirmationDialog({
          message: 'Loan amount below minimum',
          subMessage: `The requested limit is below the $${MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT.toLocaleString()} minimum for Business Term Loans. Continue anyway?`,
        }).afterClosed().subscribe(r => {
          if (r && r.readyForSubmission) {
            this.proceedWithGenerateQuote();
          }
        })
      );
      return;
    }
    this.proceedWithGenerateQuote();
  }

  private proceedWithGenerateQuote() {
    // validation for credit adjustment
    const applicationCreditAdjustment = applicationToCrediRateAdjustment(this.application);
    const currentCreditAdjustment = this.formControlCreditRateAdjustment.value!;
    if (applicationCreditAdjustment != currentCreditAdjustment) {
      if (this.isAdmin) {
        if (currentCreditAdjustment < this.ADMIN_MIN_CREDIT_RATE || currentCreditAdjustment > this.ADMIN_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.ADMIN_MIN_CREDIT_RATE} and ${this.ADMIN_MAX_CREDIT_RATE}`,
          });
          return;
        }
      } else if (this.isCreditUser) {
        if (currentCreditAdjustment < this.CREDIT_MIN_CREDIT_RATE || currentCreditAdjustment > this.CREDIT_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.CREDIT_MIN_CREDIT_RATE} and ${this.CREDIT_MAX_CREDIT_RATE}`,
          });
          return;
        }
      } else { // not admin or credit
        if (currentCreditAdjustment < this.NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE || currentCreditAdjustment > this.NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE} and ${this.NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE}`,
          });
          return;
        }
      }
    }



    this.step2Repayment = '12000';
    this.step2EstimatedDrawdownDate = moment().add(1, 'day');
    const date = moment();
    const subscription = this.applicationDialogService.openBusinessPricingComparisonDialog({
      currentValue: this.currentRstValue,
      newValue: this.calculateEstimation(date)
    }).afterClosed()
      .pipe(
        tap(r => {
          if (r && r.acceptedQuotation) {
            // update the doc fee
            this.terms!.docFee = r.docFee;
            // patch current rst docfee, save will emit events that business-finance-application-details component subscribe
            this.rst!.paymentChartData.docFee = r.docFee;
            // recreate application data for submission
            this.data = this.createApplicationData();
            this.save();
          }
        })
      ).subscribe();
    this.subscriptions.push(subscription);
  }

  canGenerateQuote(): boolean {
    const canGenerate = (
      this.canEditPricingByRole && this.formGroupStep2.valid &&
      (!this.isApplicationStagePostQa) && (!this.isContractPending)
    );
    if (!canGenerate) {
      this.markSubject.next(true);
    }
    return canGenerate;
  }


  canEditPricing(): boolean {
    return (
      this.canEditPricingByRole &&
      (!this.isApplicationStagePostQa) && (!this.isContractPending)
    );
  }

}
